import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CustomLink from 'components/Common/CustomLink/CustomLink';
import RowBuilder, {
  RowBuilderProps,
} from 'components/Common/FormBuilder/RowBuilder';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import Trans from 'components/Common/Translation/Trans';
import { useField } from 'components/Common/Formik/hooks/useField';
import { getRoutesUri } from 'services/generic';
import { ENV_NEWSLETTER_HIGHTLIGHTING_SIGNUP } from 'settings/env';
import { Field } from 'types/Field';

import styles from './RowNewsletterBuilder.module.scss';

type Props = RowBuilderProps & { field: { fields: Field[] } };

export default function RowNewsletterBuilder({
  field: { fields },
}: Props): JSX.Element {
  const { t } = useTranslation(['signUpPage']);
  const [{ value: smsCheckedValue }] = useField('newsAfibelSms');
  const [
    { value: mobileNumberValue },
    { error: isMobileError },
    { setTouched },
  ] = useField<string>('mobileNumber');

  const labelMapping = {
    newsAfibelMail: ByEmailLabel,
    newsAfibelSms: BySMSLabel,
    newsPartnerAfibelMail: ByPartnerMail,
  };

  useEffect(() => {
    if ('O' === smsCheckedValue && isMobileError) {
      setTouched(true);
    }
  }, [smsCheckedValue, isMobileError]);

  return (
    <div className={classNames(styles.container)}>
      <div className={styles.title}>{t('LABEL_NEWSLETTER_WISH_AFIBEL')}</div>
      <div className={styles.content}>
        {fields.map(field => {
          const Label = labelMapping[field.name];
          const required =
            'newsAfibelSms' === field.name
              ? field.required || mobileNumberValue.trim().length > 0
              : field.required;

          return (
            <RowBuilder
              key={field.name}
              field={{
                ...field,
                required: required,
                classes: styles => ({
                  row: styles.item,
                  container: styles.radioGroup,
                  label: classNames(
                    styles.newsletterLabel,
                    styles[`label-${field.name}`]
                  ),
                  errorMessage: styles.error,
                }),
                label: ((<Label isRequired={required} />) as unknown) as string,
              }}
              styles={styles}
            />
          );
        })}
      </div>
      <div className={styles.footer}>
        <div className={styles.dataPolicy}>
          <Trans
            i18nKey={'LABEL_NEWSLETTER_PERSONAL_DATA_POLICY'}
            namespace={'signUpPage'}
            additionalComponents={{
              lnk: (
                <CustomLink
                  to={getRoutesUri<string>('privacyPolicy')}
                  target={'_blank'}
                  rel="noreferrer"
                />
              ),
            }}
          />
        </div>
        <RequiredFields
          classes={{ wrapper: styles.requiredField, asterisk: styles.asterisk }}
        />
      </div>
    </div>
  );
}

type LabelProps = {
  isRequired: boolean;
};

function ByEmailLabel({ isRequired }: LabelProps): JSX.Element {
  return (
    <Trans
      i18nKey={
        ENV_NEWSLETTER_HIGHTLIGHTING_SIGNUP
          ? 'LABEL_NEWSLETTER_AFIBEL_EMAIL_WITH_OFFERED_MESSAGE'
          : 'LABEL_NEWSLETTER_AFIBEL_EMAIL'
      }
      values={{
        OFFERED_MESSAGE: ENV_NEWSLETTER_HIGHTLIGHTING_SIGNUP as string,
      }}
      namespace={'signUpPage'}
      additionalComponents={{
        required: (
          <span className={isRequired ? styles.labelRequired : undefined} />
        ),
        highlighting: <span className={styles.highlighting} />,
        promoOffersTcLink: (
          <CustomLink
            to={getRoutesUri<string>('promoOffers')}
            target={'_blank'}
            rel="noreferrer"
            className={styles.link}
          />
        ),
        spn: <span />,
      }}
    />
  );
}

function BySMSLabel({ isRequired }: LabelProps): JSX.Element {
  return (
    <Trans
      i18nKey={'LABEL_NEWSLETTER_AFIBEL_SMS'}
      namespace={'signUpPage'}
      additionalComponents={{
        required: (
          <span className={isRequired ? styles.labelRequired : undefined} />
        ),
      }}
    />
  );
}

function ByPartnerMail({ isRequired }: LabelProps): JSX.Element {
  return (
    <Trans
      i18nKey={'LABEL_NEWSLETTER_PARTNER_EMAIL'}
      namespace={'signUpPage'}
      additionalComponents={{
        required: (
          <span className={isRequired ? styles.labelRequired : undefined} />
        ),
      }}
    />
  );
}
